import clsx from 'clsx'
import Link from 'next/link'
import React from 'react'

import { Badge } from '@wartek-id/badge'
import { Icon } from '@wartek-id/icon'
import { Text } from '@wartek-id/text'

import { minuteToHour } from 'utils/datetime'
import { GATrackEvent } from 'utils/googleAnalytics'

import type { Module } from 'types/microlearning'

interface ModuleCardProps extends Omit<Module, 'image' | 'progress'> {
  href: string
  isCompleted: boolean
  isLocked: boolean
}

const ModuleCard = ({
  id,
  number,
  name,
  duration,
  totalMaterial,
  href,
  badgeText,
  isLocked = false,
  isCompleted = false,
}: ModuleCardProps) => {
  return (
    <div className="flex relative p-4 w-full mb-3 bg-white rounded border border-subdued h-[100%]">
      <div className="flex-grow-0 flex-shrink-0">
        {!!isCompleted ? (
          <div
            data-testid="completed-indicator"
            className="w-8 h-8 rounded-full flex items-center justify-center bg-surface-success-default"
          >
            <div className="w-5 h-5 rounded-full flex items-center justify-center bg-icon-success">
              <Icon as="i" color="inverse" fontSize="sm">
                check
              </Icon>
            </div>
          </div>
        ) : (
          <div
            className={clsx(
              'flex justify-center items-center w-8 h-8 rounded-full overflow-hidden',
              isLocked ? 'bg-icon-disabled' : 'bg-informational text-white'
            )}
          >
            {isLocked ? (
              <Icon data-testid="locked-indicator" as="i" fontSize="sm">
                lock
              </Icon>
            ) : (
              <span>{number}</span>
            )}
          </div>
        )}
      </div>
      <div className="ml-4 flex-auto">
        <div>
          <Text variant="body-sm" color={isLocked ? 'disabled' : 'default'}>
            Modul {number}
          </Text>
        </div>
        <div className={clsx(!isCompleted && 'mb-2')}>
          {isLocked ? (
            <Text variant="heading-md" color="disabled">
              {name}
            </Text>
          ) : (
            <Link href={href} passHref legacyBehavior>
              <Text
                data-module-id={id}
                href={href}
                className="overlay-link"
                as="a"
                variant="heading-md"
                onClick={() => {
                  GATrackEvent({
                    eventName: 'learning_activity',
                    eventParams: {
                      event_trigger: 'module_click_from_topik_page',
                      action_type: 'choose_module',
                      meta: JSON.stringify({
                        module_id: id,
                      }),
                    },
                    sendDefaultParams: true,
                  })
                }}
              >
                {name}
              </Text>
            </Link>
          )}
        </div>
        {!isCompleted && (
          <div className="flex -mx-2 mb-4">
            <div className="flex items-center px-2">
              <Icon
                as="i"
                color={isLocked ? 'disabled' : 'default'}
                fontSize="sm"
              >
                access_time
              </Icon>
              <div className="ml-2">
                <Text as="span" variant="body-sm" color="subdued">
                  {minuteToHour(duration)}
                </Text>
              </div>
            </div>
            <div className="flex items-center px-2">
              <Icon
                as="i"
                color={isLocked ? 'disabled' : 'default'}
                fontSize="sm"
              >
                book
              </Icon>
              <div className="ml-2">
                <Text as="span" variant="body-sm" color="subdued">
                  {totalMaterial} materi
                </Text>
              </div>
            </div>
          </div>
        )}
        {!!badgeText && (
          <div>
            <Badge
              variant={
                badgeText.toLowerCase() !== 'kerjakan ulang post test'
                  ? 'informational'
                  : 'warning'
              }
            >
              {badgeText}
            </Badge>
          </div>
        )}
      </div>
    </div>
  )
}

export default ModuleCard
